// Copyright (c) Ping. All rights reserved.
//
// "New meeting" composer (create + edit) — Figma dark modal. Emits a
// MeetingDraft the host turns into the §2.13 `com.ping.meeting` / `_update`
// envelope. Built from RN primitives + NativeWind on the shared `@ping/ui`
// Modal. Type is a radio group; Start/End open the SHARED cross-platform
// `DateTimePicker` calendar (the same control the Schedule-message modal uses)
// via an anchored popover — the trigger fields carry a lucide Calendar / Clock
// icon so they read as pickers without a raw `<input type=date>`.

import React, { useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native';

import { Calendar, Clock, X } from 'lucide-react-native';

import { AnchoredPopover,Modal } from '@ping/ui';
import { DateTimePicker } from '@ping/ui/src/composer/DateTimePicker';
import { tokens } from '@ping/ui/src/design-system/tokens';
import { useColorScheme } from '@ping/ui/src/foundations/colorScheme';

export type MeetingKind = 'meeting' | 'call';

/** What the modal hands back on submit — wall-clock ms, plain strings. */
export interface MeetingDraft {
  readonly kind: MeetingKind;
  readonly title: string;
  readonly description?: string;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly location?: string;
  readonly meetingLink?: string;
  /** Device-local reminder (minutes before); not on the wire. */
  readonly reminderMinutes?: number;
  /** Present when kind === 'call' — attach a Ping call to the meeting. */
  readonly autoCallMedia?: 'audio' | 'video';
}

export interface MeetingModalInitial {
  readonly kind?: MeetingKind;
  readonly title?: string;
  readonly description?: string;
  readonly startsAt?: number;
  readonly endsAt?: number;
  readonly location?: string;
  readonly meetingLink?: string;
  readonly reminderMinutes?: number;
  readonly autoCallMedia?: 'audio' | 'video';
}

interface MeetingModalProps {
  readonly visible: boolean;
  readonly mode: 'create' | 'edit';
  readonly initial?: MeetingModalInitial;
  readonly onClose: () => void;
  readonly onSubmit: (draft: MeetingDraft) => Promise<void> | void;
}

/** "25/07/2026, 10:48 AM" — the compact value shown in a date/time field. */
function formatFieldValue(d: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function toDate(ms: number): Date {
  const d = new Date(ms);
  d.setSeconds(0, 0);
  return d;
}

const REMINDER_OPTIONS: ReadonlyArray<{ readonly label: string; readonly minutes: number | undefined }> = [
  { label: 'None', minutes: undefined },
  { label: '5m', minutes: 5 },
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '1d', minutes: 1440 },
];

/** A labelled radio option (circle + inner dot), matching the design. */
function RadioOption({
  label,
  selected,
  disabled,
  onPress,
  testID,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onPress: () => void;
  readonly testID: string;
}): React.ReactElement {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityState={{ selected, disabled }}
      className={`flex-row items-center gap-2 py-1 ${disabled && !selected ? 'opacity-40' : 'active:opacity-70'}`}
      testID={testID}
    >
      <View
        className={`h-[18px] w-[18px] rounded-full border-2 items-center justify-center ${
          selected
            ? 'border-light-accent-indigo dark:border-[#537EFF]'
            : 'border-light-border-secondary dark:border-[#6B7280]'
        }`}
      >
        {selected ? <View className="h-[9px] w-[9px] rounded-full bg-light-accent-indigo dark:bg-[#537EFF]" /> : null}
      </View>
      <Text className="font-sans text-[15px] text-light-text-primary dark:text-white">{label}</Text>
    </Pressable>
  );
}

/** A field that opens the shared calendar + time picker in an anchored popover. */
function DateTimeField({
  label,
  icon,
  value,
  minDate,
  onChange,
  glyphColor,
  testID,
}: {
  readonly label: string;
  readonly icon: 'date' | 'time';
  readonly value: Date;
  readonly minDate?: Date | undefined;
  readonly onChange: (next: Date) => void;
  readonly glyphColor: string;
  readonly testID: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<View>(null);
  const Icon = icon === 'date' ? Calendar : Clock;
  return (
    <View className="flex-1 gap-2">
      <Text className="font-sans text-[12px] text-light-text-secondary dark:text-[#8C929E]">{label}</Text>
      <View ref={fieldRef}>
        <Pressable
          onPress={() => setOpen((o) => !o)}
          accessibilityRole="button"
          accessibilityState={{ expanded: open }}
          className="flex-row items-center justify-between rounded-[12px] border border-light-border-secondary dark:border-[#222429] px-[14px] py-[13px] active:opacity-80"
          testID={testID}
        >
          <Text className="font-sans text-[15px] text-light-text-primary dark:text-[#F5F6F8]">
            {formatFieldValue(value)}
          </Text>
          <Icon size={18} strokeWidth={2} color={glyphColor} />
        </Pressable>
      </View>
      <AnchoredPopover
        anchorRef={fieldRef}
        open={open}
        onClose={() => setOpen(false)}
        placement="bottom"
        align="start"
        gap={6}
        trapFocus
      >
        <View className="w-[300px] max-w-full rounded-[12px] p-3 bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.08]">
          <DateTimePicker
            value={value}
            onChange={onChange}
            {...(minDate !== undefined ? { minDate } : {})}
            testID={`${testID}-picker`}
          />
        </View>
      </AnchoredPopover>
    </View>
  );
}

export default function MeetingModal({
  visible,
  mode,
  initial,
  onClose,
  onSubmit,
}: MeetingModalProps): React.ReactElement {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];
  const glyphColor = colors.text.secondary;

  const defaults = useMemo(() => {
    const start = initial?.startsAt ?? Date.now() + 3600_000;
    const end = initial?.endsAt ?? start + 3600_000;
    return { start, end };
  }, [initial?.startsAt, initial?.endsAt]);

  const [kind, setKind] = useState<MeetingKind>(initial?.kind ?? 'meeting');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [startsAt, setStartsAt] = useState<Date>(() => toDate(defaults.start));
  const [endsAt, setEndsAt] = useState<Date>(() => toDate(defaults.end));
  const [location, setLocation] = useState(initial?.location ?? '');
  const [meetingLink, setMeetingLink] = useState(initial?.meetingLink ?? '');
  const [reminderMinutes, setReminderMinutes] = useState<number | undefined>(
    initial?.reminderMinutes ?? (mode === 'create' ? 30 : undefined),
  );
  const [autoCallMedia, setAutoCallMedia] = useState<'audio' | 'video'>(
    initial?.autoCallMedia ?? 'audio',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submitLabel = mode === 'edit' ? 'Save' : 'Create';
  const typeLocked = mode === 'edit';

  const applyStart = (next: Date): void => {
    setStartsAt(next);
    setEndsAt((prevEnd) => (next.getTime() >= prevEnd.getTime() ? new Date(next.getTime() + 3600_000) : prevEnd));
  };

  async function handleSubmit(): Promise<void> {
    setError(null);
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    const startMs = startsAt.getTime();
    const endMs = endsAt.getTime();
    if (endMs <= startMs) {
      setError('End must be after start');
      return;
    }
    if (mode === 'create' && startMs < Date.now()) {
      setError('Meeting cannot be scheduled in the past');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        kind,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        startsAt: startMs,
        endsAt: endMs,
        ...(location.trim() ? { location: location.trim() } : {}),
        ...(kind === 'meeting' && meetingLink.trim() ? { meetingLink: meetingLink.trim() } : {}),
        ...(reminderMinutes !== undefined ? { reminderMinutes } : {}),
        ...(kind === 'call' ? { autoCallMedia } : {}),
      });
      // Create mode reuses ONE always-mounted modal (the composer overlay just
      // toggles `visible`), so its fields would otherwise persist into the next
      // "New meeting". Clear them back to defaults after a successful create.
      if (mode === 'create') {
        const nextStart = Date.now() + 3600_000;
        setKind('meeting');
        setTitle('');
        setDescription('');
        setLocation('');
        setMeetingLink('');
        setStartsAt(toDate(nextStart));
        setEndsAt(toDate(nextStart + 3600_000));
        setReminderMinutes(30);
        setAutoCallMedia('audio');
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save meeting');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal visible={visible} maxWidth={600} onClose={onClose} accessibilityLabel="New meeting">
      <View className="w-[432px] max-w-full">
        {/* Header */}
        <View className="flex-row items-center justify-between">
          <Text className="font-sans text-[22px] font-bold text-light-text-primary dark:text-white">
            {mode === 'edit' ? 'Edit meeting' : 'New meeting'}
          </Text>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close"
            className="w-8 h-8 items-center justify-center rounded-full bg-light-surface-secondary dark:bg-[#2A2D34] active:opacity-70"
            testID="meeting-modal-close"
          >
            <X size={18} strokeWidth={2} color={colors.text.secondary} />
          </Pressable>
        </View>

        <ScrollView className="mt-5 max-h-[70vh]" keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <View className="gap-[18px]">
            {/* Type — radio group; immutable after create (update wire has no type). */}
            <View className="gap-2">
              <Text className="font-sans text-[12px] text-light-text-secondary dark:text-[#8C929E]">Type</Text>
              <View className="flex-row gap-6">
                <RadioOption label="Meeting" selected={kind === 'meeting'} disabled={busy || typeLocked} onPress={() => setKind('meeting')} testID="meeting-kind-meeting" />
                <RadioOption label="Ping call" selected={kind === 'call'} disabled={busy || typeLocked} onPress={() => setKind('call')} testID="meeting-kind-call" />
              </View>
            </View>

            {/* Title */}
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="Title"
              placeholderTextColor={colors.text.secondary}
              editable={!busy}
              className="rounded-[12px] border border-light-border-secondary dark:border-[#222429] px-[14px] h-12 font-sans text-[15px] text-light-text-primary dark:text-[#F5F6F8]"
              testID="meeting-title"
            />

            {/* Description */}
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Description"
              placeholderTextColor={colors.text.secondary}
              editable={!busy}
              multiline
              numberOfLines={3}
              className="rounded-[12px] border border-light-border-secondary dark:border-[#222429] px-[14px] py-3 min-h-[88px] font-sans text-[15px] text-light-text-primary dark:text-[#F5F6F8]"
              style={{ textAlignVertical: 'top' }}
              testID="meeting-description"
            />

            {/* Start / End — open the shared calendar picker. */}
            <View className="flex-row gap-4">
              <DateTimeField label="Start date & time" icon="date" value={startsAt} minDate={mode === 'create' ? new Date() : undefined} onChange={applyStart} glyphColor={glyphColor} testID="meeting-starts" />
              <DateTimeField label="End date & time" icon="time" value={endsAt} minDate={startsAt} onChange={setEndsAt} glyphColor={glyphColor} testID="meeting-ends" />
            </View>

            {/* Location */}
            <TextInput
              value={location}
              onChangeText={setLocation}
              placeholder="Location"
              placeholderTextColor={colors.text.secondary}
              editable={!busy}
              className="rounded-[12px] border border-light-border-secondary dark:border-[#222429] px-[14px] h-12 font-sans text-[15px] text-light-text-primary dark:text-[#F5F6F8]"
              testID="meeting-location"
            />

            {/* Meeting link (meeting) OR call type (call, create only). */}
            {kind === 'meeting' ? (
              <TextInput
                value={meetingLink}
                onChangeText={setMeetingLink}
                placeholder="Meeting link"
                placeholderTextColor={colors.text.secondary}
                editable={!busy}
                autoCapitalize="none"
                className="rounded-[12px] border border-light-border-secondary dark:border-[#222429] px-[14px] h-12 font-sans text-[15px] text-light-text-primary dark:text-[#F5F6F8]"
                testID="meeting-link"
              />
            ) : mode === 'create' ? (
              <View className="gap-2">
                <Text className="font-sans text-[12px] text-light-text-secondary dark:text-[#8C929E]">Call type</Text>
                <View className="flex-row gap-6">
                  <RadioOption label="Voice" selected={autoCallMedia === 'audio'} disabled={busy} onPress={() => setAutoCallMedia('audio')} testID="meeting-callmedia-audio" />
                  <RadioOption label="Video" selected={autoCallMedia === 'video'} disabled={busy} onPress={() => setAutoCallMedia('video')} testID="meeting-callmedia-video" />
                </View>
              </View>
            ) : null}

            {/* Reminder */}
            <View className="gap-2">
              <Text className="font-sans text-[12px] text-light-text-secondary dark:text-[#8C929E]">Reminder</Text>
              <View className="flex-row flex-wrap gap-2">
                {REMINDER_OPTIONS.map((opt) => {
                  const active = reminderMinutes === opt.minutes;
                  return (
                    <Pressable
                      key={opt.label}
                      onPress={() => setReminderMinutes(opt.minutes)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      className={`rounded-pill border px-4 py-[9px] active:opacity-80 ${
                        active
                          ? 'border-light-accent-indigo dark:border-[#537EFF] bg-light-accent-indigo/10 dark:bg-[#537EFF]/15'
                          : 'border-light-border-secondary dark:border-[#222429]'
                      }`}
                      testID={`meeting-reminder-${opt.label}`}
                    >
                      <Text
                        className={`font-sans text-[13px] font-semibold ${
                          active ? 'text-light-accent-indigo dark:text-[#8FA9FF]' : 'text-light-text-primary dark:text-white'
                        }`}
                      >
                        {opt.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            {error !== null ? <Text className="font-sans text-[13px] text-[#E5484D]">{error}</Text> : null}
          </View>
        </ScrollView>

        {/* Footer */}
        <View className="flex-row items-center justify-end gap-4 pt-5">
          <Pressable onPress={onClose} disabled={busy} accessibilityRole="button" className="px-2 py-[10px] active:opacity-60" testID="meeting-modal-cancel">
            <Text className="font-sans text-[15px] font-semibold text-light-text-primary dark:text-white">Cancel</Text>
          </Pressable>
          <Pressable
            onPress={() => void handleSubmit()}
            disabled={busy}
            accessibilityRole="button"
            className={`rounded-pill bg-light-accent-indigo dark:bg-[#537EFF] px-7 py-[12px] active:opacity-90 ${busy ? 'opacity-60' : ''}`}
            testID="meeting-modal-submit"
          >
            <Text className="font-sans text-[15px] font-bold text-white">{busy ? 'Saving…' : submitLabel}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
